/**
 * BrokerIntegrationTab — Broker Integration for Admin
 * =========================================================
 * Session 136: Initial UI layout
 * Session 137: Unlocked fields, wired save/load/test to backend
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Link2, Power, PowerOff, Shield, AlertTriangle, 
  Check, Settings, ChevronDown, Clock, Info,
  Save, Loader2, CheckCircle2, XCircle, Wifi,
  TrendingUp, TrendingDown, RefreshCw, Target, Ban
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BROKERS = [
  { 
    id: 'zerodha', name: 'Zerodha', subtitle: 'Kite Connect API', 
    color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20',
    status: 'first',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'text', placeholder: 'Your Kite Connect API Key', help: 'From developers.kite.trade > My Apps' },
      { key: 'api_secret', label: 'API Secret', type: 'password', placeholder: 'Your Kite Connect API Secret', help: 'Keep this secure, never share publicly' },
      { key: 'client_id', label: 'Zerodha Client ID', type: 'text', placeholder: 'e.g., AB1234', help: 'Your Zerodha trading account ID' },
    ],
    authFlow: 'OAuth redirect — daily login via Kite login page to get access token',
    docsUrl: 'https://kite.trade/docs/connect/v3/',
    note: 'Access token expires at end of day. Requires daily re-authentication via browser login.',
  },
  { 
    id: 'angelone', name: 'Angel One', subtitle: 'SmartAPI', 
    color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20',
    status: 'planned',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'text', placeholder: 'Your SmartAPI Key', help: 'From smartapi.angelbroking.com' },
      { key: 'client_id', label: 'Client Code', type: 'text', placeholder: 'e.g., DELL2023', help: 'Your Angel One client code' },
      { key: 'password', label: 'Password / MPIN', type: 'password', placeholder: 'Trading terminal password', help: 'Your trading password or MPIN' },
      { key: 'totp_secret', label: 'TOTP Secret Key', type: 'password', placeholder: 'Secret from authenticator setup', help: 'From smartapi.angelbroking.com/enable-totp' },
    ],
    authFlow: 'Auto-login with TOTP — no daily browser login needed',
    docsUrl: 'https://smartapi.angelone.in/',
    note: 'TOTP secret enables fully automated daily login without manual intervention.',
  },
  { 
    id: 'dhan', name: 'Dhan', subtitle: 'DhanHQ API', 
    color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20',
    status: 'active',
    fields: [
      { key: 'client_id', label: 'Dhan Client ID', type: 'text', placeholder: 'Your Dhan Client ID', help: 'From web.dhan.co > My Profile' },
      { key: 'access_token', label: 'Access Token', type: 'password', placeholder: 'Daily access token', help: 'Generate from My Profile > Access DhanHQ APIs' },
    ],
    authFlow: 'Daily token generation from Dhan web portal (24-hour validity)',
    docsUrl: 'https://dhanhq.co/docs/v2/',
    note: 'Token valid for 24 hours only. Must regenerate daily before market open.',
  },
  { 
    id: 'fyers', name: 'Fyers', subtitle: 'Fyers API v3', 
    color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20',
    status: 'planned',
    fields: [
      { key: 'app_id', label: 'App ID (Client ID)', type: 'text', placeholder: 'e.g., L9NY305RTW-200', help: 'From fyers.in API Dashboard' },
      { key: 'secret_key', label: 'Secret Key', type: 'password', placeholder: 'Your Fyers Secret Key', help: 'Paired with App ID' },
      { key: 'redirect_uri', label: 'Redirect URI', type: 'text', placeholder: 'https://trade.fyers.in/api-login/redirect-uri/index.html', help: 'Must match exactly what was set in app creation' },
      { key: 'fyers_id', label: 'Fyers ID (Username)', type: 'text', placeholder: 'e.g., FY12345', help: 'Your Fyers trading account ID' },
      { key: 'totp_key', label: 'TOTP Key', type: 'password', placeholder: 'TOTP secret for auto-login', help: 'From authenticator app setup' },
      { key: 'pin', label: 'PIN', type: 'password', placeholder: '4-digit PIN', help: 'Your Fyers 4-digit PIN' },
    ],
    authFlow: 'OAuth + TOTP — automated login with stored credentials',
    docsUrl: 'https://myapi.fyers.in',
    note: 'Apps must be activated post April 2026 (new App ID ending -200).',
  },
  { 
    id: 'upstox', name: 'Upstox', subtitle: 'Upstox API v2', 
    color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20',
    status: 'planned',
    fields: [
      { key: 'api_key', label: 'API Key (Client ID)', type: 'text', placeholder: 'Your UpLink API Key', help: 'From developer.upstox.com > Your Apps' },
      { key: 'api_secret', label: 'API Secret', type: 'password', placeholder: 'Your UpLink API Secret', help: 'Keep secure, never share' },
      { key: 'redirect_uri', label: 'Redirect URI', type: 'text', placeholder: 'e.g., http://localhost:8000', help: 'Must match exactly what was set in app' },
    ],
    authFlow: 'OAuth redirect — daily login via Upstox login page',
    docsUrl: 'https://upstox.com/developer/api-documentation/',
    note: 'Access token expires daily. Requires browser-based re-authentication each morning.',
  },
];

const SECURITY_TYPES = [
  { id: 'nifty_options', label: 'NIFTY Options', description: 'NIFTY CE/PE', defaultBuy: true, defaultSell: false, defaultLots: 1, group: 'options' },
  { id: 'banknifty_options', label: 'BANKNIFTY Options', description: 'BANKNIFTY CE/PE', defaultBuy: false, defaultSell: false, defaultLots: 1, group: 'options' },
  { id: 'nifty_futures', label: 'NIFTY Futures', description: 'NIFTY-I', defaultBuy: false, defaultSell: false, defaultLots: 1, group: 'futures' },
  { id: 'banknifty_futures', label: 'BANKNIFTY Futures', description: 'BANKNIFTY-I', defaultBuy: false, defaultSell: false, defaultLots: 1, group: 'futures' },
  { id: 'stock_futures', label: 'Stock Futures', description: 'SMLT stock signals', defaultBuy: false, defaultSell: false, defaultLots: 1, group: 'stocks' },
];

const BrokerIntegrationTab = () => {
  const { authFetch, user } = useAuth();
  const [dailyActive, setDailyActive] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState('dhan');
  const [showBrokerDropdown, setShowBrokerDropdown] = useState(false);
  const [maxTrades, setMaxTrades] = useState(3);
  const [slippageBuffer, setSlippageBuffer] = useState(3.0);
  const [autoSlOrder, setAutoSlOrder] = useState(true);
  const [eodSquareOff, setEodSquareOff] = useState('15:10');
  const [preferences, setPreferences] = useState(
    SECURITY_TYPES.reduce((acc, sec) => ({
      ...acc,
      [sec.id]: { enabled: sec.id === 'nifty_options', buy: sec.defaultBuy, sell: sec.defaultSell, lots: sec.defaultLots }
    }), {})
  );
  const [showSellWarning, setShowSellWarning] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [prefsDirty, setPrefsDirty] = useState(false);

  // WIP overlay state
  // Apr-2026 B-UX-1: Persist the user's last-active broker in localStorage
  // and default to it on load. If Dhan is already configured/active, prefer
  // that. Prevents having to re-click "Dhan" every time user opens this tab.
  const _loadInitialWipBroker = () => {
    try {
      const saved = localStorage.getItem('algogreeks_wip_broker');
      if (saved && BROKERS.some(b => b.id === saved)) return saved;
    } catch (_e) { /* ignore */ }
    return 'dhan';  // Safer default (was 'zerodha' which forced user to re-click)
  };
  const [wipBroker, setWipBroker] = useState(_loadInitialWipBroker);
  // Apr-2026 B-UX-1: dropdown visibility for the "Change Broker" widget
  const [showChangeBrokerMenu, setShowChangeBrokerMenu] = useState(false);
  const [wipCredentials, setWipCredentials] = useState({});
  const [savedBrokers, setSavedBrokers] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loadingCreds, setLoadingCreds] = useState(true);
  const [dhanConnected, setDhanConnected] = useState(false);  // Dhan-specific: form closes when true
  // Session 147j+17l — Live token-health state. `dhanConnected` only
  // says "credentials exist in DB"; this state reflects whether the
  // stored token actually passes Dhan's /v2/ip/getIP live probe.
  // Prevents the "green pill lie" where the UI showed Connected while
  // the JWT had been expired for hours.
  const [dhanHealth, setDhanHealth] = useState({
    status: 'unknown',
    message: '',
    last_check: null,
    checking: false,
  });
  const [dhanIp, setDhanIp] = useState({ primary: '', secondary: '', loading: false, error: '' });
  const [registeringIp, setRegisteringIp] = useState(false);
  const [detectedIp, setDetectedIp] = useState({ ip: '', loading: false });

  const wipBrokerData = BROKERS.find(b => b.id === wipBroker);
  const broker = BROKERS.find(b => b.id === selectedBroker);
  const currentSavedBroker = savedBrokers[wipBroker];
  const hasSellEnabled = Object.values(preferences).some(p => p.sell);

  // Load saved broker credential status on mount
  const loadSavedCredentials = useCallback(async () => {
    try {
      setLoadingCreds(true);
      const res = await authFetch(`${API}/api/admin/broker/credentials`);
      if (res.ok) {
        const data = await res.json();
        setSavedBrokers(data.brokers || {});
        // If Dhan is already configured, mark as connected
        if (data.brokers?.dhan?.configured) {
          setDhanConnected(true);
        }
        // 147j+17z — Auto-populate non-sensitive identifier fields
        // (client_id, api_key, username, app_id, user_id) from saved
        // credentials so users don't retype permanent account IDs
        // during daily token refresh. Backend returns these in
        // plaintext (they're not secrets — like a username).
        const PLAINTEXT_SAFE = new Set(['client_id', 'api_key', 'username', 'app_id', 'user_id']);
        const prefills = {};
        for (const [bid, info] of Object.entries(data.brokers || {})) {
          if (!info?.configured || !info.fields) continue;
          const bucket = {};
          for (const [k, v] of Object.entries(info.fields)) {
            // Skip masked values (contain • dot chars) — those are
            // secret fields we can't pre-populate.
            if (PLAINTEXT_SAFE.has(k) && typeof v === 'string' && !v.includes('●')) {
              bucket[k] = v;
            }
          }
          if (Object.keys(bucket).length > 0) prefills[bid] = bucket;
        }
        if (Object.keys(prefills).length > 0) {
          setWipCredentials(prev => {
            const next = { ...prev };
            for (const [bid, bucket] of Object.entries(prefills)) {
              next[bid] = { ...(next[bid] || {}), ...bucket };
            }
            return next;
          });
        }
      }
    } catch (err) {
      console.error('Failed to load broker credentials:', err);
    } finally {
      setLoadingCreds(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadSavedCredentials();
  }, [loadSavedCredentials]);

  // Fetch current whitelisted IPs from Dhan
  const fetchDhanIp = useCallback(async () => {
    try {
      setDhanIp(prev => ({ ...prev, loading: true, error: '' }));
      const res = await authFetch(`${API}/api/broker/dhan/ip`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'ok') {
          setDhanIp({ primary: data.primary_ip || '', secondary: data.secondary_ip || '', loading: false, error: '' });
        } else {
          setDhanIp(prev => ({ ...prev, loading: false, error: data.message || 'Failed to fetch IP' }));
        }
      }
    } catch (err) {
      setDhanIp(prev => ({ ...prev, loading: false, error: 'Failed to fetch IP status' }));
    }
  }, [authFetch]);

  // Fetch IPs when Dhan is connected
  useEffect(() => {
    if (dhanConnected) {
      fetchDhanIp();
      detectServerIp();
    }
  }, [dhanConnected, fetchDhanIp]);

  // Session 147j+17l — Live token-health probe. Fires when the tab
  // mounts, when the user reconnects, and every 60s while the tab is
  // open. Backend `/api/broker/dhan/health` returns
  // `status: 'healthy' | 'token_expired' | 'ip_mismatch' | ...` based
  // on a real Dhan /v2/ip/getIP call.
  const fetchDhanHealth = useCallback(async () => {
    if (!dhanConnected) return;
    setDhanHealth(prev => ({ ...prev, checking: true }));
    try {
      const res = await authFetch(`${API}/api/broker/dhan/health`);
      if (res.ok) {
        const data = await res.json();
        setDhanHealth({
          status: data.status || 'unknown',
          message: data.message || '',
          last_check: data.last_check,
          checking: false,
        });
      } else {
        setDhanHealth(prev => ({ ...prev, checking: false }));
      }
    } catch (_err) {
      setDhanHealth(prev => ({ ...prev, checking: false }));
    }
  }, [dhanConnected, authFetch]);

  useEffect(() => {
    if (!dhanConnected) return;
    fetchDhanHealth();
    const iv = setInterval(fetchDhanHealth, 60000);
    return () => clearInterval(iv);
  }, [dhanConnected, fetchDhanHealth]);

  // Auto-detect server's public IP
  const detectServerIp = async () => {
    try {
      setDetectedIp(prev => ({ ...prev, loading: true }));
      const res = await authFetch(`${API}/api/broker/server-ip`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'ok') {
          setDetectedIp({ ip: data.ip, loading: false });
        } else {
          setDetectedIp({ ip: '', loading: false });
        }
      }
    } catch {
      setDetectedIp({ ip: '', loading: false });
    }
  };

  // Register server IP with Dhan (one-click)
  const handleRegisterIp = async () => {
    try {
      setRegisteringIp(true);
      const res = await authFetch(`${API}/api/broker/dhan/ip/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: detectedIp.ip, ip_flag: 'PRIMARY' }),
      });
      const data = await res.json();
      if (data.status === 'ok') {
        toast.success(data.message);
        // Update IP state directly from response (Dhan's GET may lag)
        setDhanIp(prev => ({ ...prev, primary: data.ip || detectedIp.ip, error: '' }));
      } else {
        toast.error(data.message || 'IP registration failed');
      }
    } catch (err) {
      toast.error('IP registration failed');
    } finally {
      setRegisteringIp(false);
    }
  };

  // Load trading preferences from MongoDB
  const loadPreferences = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/trading/preferences`);
      if (res.ok) {
        const data = await res.json();
        if (data.preferences) setPreferences(data.preferences);
        if (data.max_trades_per_day) setMaxTrades(data.max_trades_per_day);
        if (data.daily_active !== undefined) setDailyActive(data.daily_active);
        if (data.slippage_buffer_pct) setSlippageBuffer(data.slippage_buffer_pct);
        if (data.auto_sl_order !== undefined) setAutoSlOrder(data.auto_sl_order);
        if (data.eod_square_off_time) setEodSquareOff(data.eod_square_off_time);
        if (data.selected_broker) {
          setSelectedBroker(data.selected_broker);
          // Apr-2026 B-UX-1: also align the WIP broker so the form shows
          // the saved broker immediately (no manual re-click needed).
          setWipBroker(data.selected_broker);
          try { localStorage.setItem('algogreeks_wip_broker', data.selected_broker); } catch (_e) { /* ignore */ }
        }
        setPrefsLoaded(true);
        setPrefsDirty(false);
      }
    } catch (err) {
      console.error('Failed to load trading preferences:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Save trading preferences to MongoDB
  const savePreferences = async () => {
    try {
      setSavingPrefs(true);
      const res = await authFetch(`${API}/api/trading/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferences,
          max_trades_per_day: maxTrades,
          daily_active: dailyActive,
          slippage_buffer_pct: slippageBuffer,
          selected_broker: selectedBroker,
          auto_sl_order: autoSlOrder,
          eod_square_off_time: eodSquareOff,
        }),
      });
      if (res.ok) {
        toast.success('Trading preferences saved');
        setPrefsDirty(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save preferences');
      }
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  // Toggle daily activation (instant save)
  const handleDailyToggle = async (active) => {
    setDailyActive(active);
    try {
      await authFetch(`${API}/api/trading/preferences/daily-toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ daily_active: active }),
      });
      toast.success(active ? 'Auto-Trading activated for today' : 'Auto-Trading deactivated');
    } catch (err) {
      toast.error('Failed to update daily activation');
      setDailyActive(!active);
    }
  };

  // Mark preferences dirty when any value changes
  const updatePreference = (secId, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [secId]: { ...prev[secId], [field]: value }
    }));
    setPrefsDirty(true);
  };

  // Clear test result when switching brokers
  useEffect(() => {
    setTestResult(null);
  }, [wipBroker]);

  const handleSave = async () => {
    const creds = wipCredentials[wipBroker] || {};
    const hasValues = Object.values(creds).some(v => v && v.trim());
    if (!hasValues) {
      toast.error('Enter at least one credential field');
      return;
    }

    try {
      setSaving(true);
      const res = await authFetch(`${API}/api/admin/broker/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ broker_id: wipBroker, credentials: creds }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(`${wipBrokerData.name} credentials saved securely`);
      // Clear typed values and reload masked versions
      setWipCredentials(prev => ({ ...prev, [wipBroker]: {} }));
      setTestResult(null);
      await loadSavedCredentials();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const res = await authFetch(`${API}/api/admin/broker/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ broker_id: wipBroker }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTestResult({ status: 'error', message: data.detail || 'Test failed' });
      } else {
        setTestResult(data);
        // If Dhan is connected, mark as connected and close form
        if (data.status === 'connected' && wipBroker === 'dhan') {
          setDhanConnected(true);
          toast.success('Dhan connected successfully!');
        }
      }
    } catch (err) {
      setTestResult({ status: 'error', message: err.message });
    } finally {
      setTesting(false);
    }
  };

  // SESSION 137: Combined Save + Validate for active brokers (Dhan)
  // Form stays open until connection is validated
  const handleSaveAndValidate = async () => {
    const creds = wipCredentials[wipBroker] || {};
    const hasValues = Object.values(creds).some(v => v && v.trim());
    if (!hasValues) {
      toast.error('Enter both Client ID and Access Token');
      return;
    }

    // Session 147j+17m — Allow partial updates. If credentials already
    // exist server-side for this broker, only require that AT LEAST
    // one field is filled in (typically access_token during a daily
    // token refresh). If no creds exist yet, all fields required.
    const brokerData = BROKERS.find((b) => b.id === wipBroker);
    const hasExistingCreds = !!savedBrokers[wipBroker]?.configured;
    if (hasExistingCreds) {
      const hasAnyValue = (brokerData?.fields || []).some(
        (f) => (creds[f.key] || "").trim(),
      );
      if (!hasAnyValue) {
        toast.error("Enter the field you want to update (e.g. Access Token)");
        return;
      }
    } else {
      const missingFields = (brokerData?.fields || []).filter(
        (f) => !creds[f.key]?.trim(),
      );
      if (missingFields.length > 0) {
        toast.error(`Please fill: ${missingFields.map((f) => f.label).join(", ")}`);
        return;
      }
    }

    try {
      setSaving(true);
      setTestResult(null);

      // Step 1: Save credentials
      const saveRes = await authFetch(`${API}/api/admin/broker/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ broker_id: wipBroker, credentials: creds }),
      });
      if (!saveRes.ok) {
        const err = await saveRes.json();
        throw new Error(err.detail || 'Failed to save credentials');
      }

      // Step 2: Validate connection (live API call for Dhan)
      setTesting(true);
      const testRes = await authFetch(`${API}/api/admin/broker/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ broker_id: wipBroker }),
      });
      const testData = await testRes.json();

      if (testData.status === 'connected') {
        // SUCCESS — connection validated
        setTestResult(testData);
        setDhanConnected(true);
        setWipCredentials(prev => ({ ...prev, [wipBroker]: {} }));
        await loadSavedCredentials();
        // Session 145: pulse the app-wide health banner so it re-checks
        // /api/broker/dhan/health right now instead of waiting up to 60s
        // for its next scheduled poll. Without this the red "token
        // expired" banner lingered after a successful reconnect.
        try { window.dispatchEvent(new Event('dhan-health-refresh')); } catch (_e) { /* no-op */ }
        toast.success(`${brokerData.name} connected! Balance: Rs.${testData.available_balance?.toLocaleString('en-IN')}`);
      } else if (testData.status === 'warning' || testData.status === 'error') {
        // FAILED — keep form open
        setTestResult(testData);
        toast.error(testData.issues?.[0] || testData.message || 'Validation failed');
      } else {
        // Other status (ok but not 'connected' — format-only validation)
        setTestResult(testData);
        await loadSavedCredentials();
        setWipCredentials(prev => ({ ...prev, [wipBroker]: {} }));
      }
    } catch (err) {
      setTestResult({ status: 'error', message: err.message, issues: [err.message] });
      toast.error(err.message);
    } finally {
      setSaving(false);
      setTesting(false);
    }
  };

  const handleSellToggle = (secId) => {
    const newVal = !preferences[secId].sell;
    updatePreference(secId, 'sell', newVal);
    if (newVal) setShowSellWarning(true);
  };

  // Check if user has typed new (unsaved) values for current broker
  const hasUnsavedChanges = Object.values(wipCredentials[wipBroker] || {}).some(v => v && v.trim());
  
  // Is the selected broker "active" (live integration) or "planned" (WIP)
  const isActiveBroker = wipBrokerData?.status === 'active';
  // Should the overlay show? Hide if Dhan is connected
  const showOverlay = !dhanConnected || wipBroker !== 'dhan';

  return (
    <div className="relative" data-testid="broker-integration-tab">
      {/* WIP Overlay — Shows for planned brokers or until Dhan connects */}
      {showOverlay && (
      <div className="absolute inset-0 z-20 bg-black/50 backdrop-blur-[2px] flex items-start justify-center pt-8 pointer-events-auto rounded-lg overflow-y-auto pb-8">
        <div className={`bg-gray-900 border ${isActiveBroker ? 'border-cyan-500/30' : 'border-amber-500/30'} rounded-xl p-6 max-w-lg w-full shadow-2xl ${isActiveBroker ? 'shadow-cyan-500/5' : 'shadow-amber-500/5'} mx-4`} data-testid="wip-overlay">
          {/* Header */}
          <div className="text-center mb-5">
            <div className={`w-14 h-14 rounded-full ${isActiveBroker ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-amber-500/10 border-amber-500/20'} border flex items-center justify-center mx-auto mb-4`}>
              {isActiveBroker
                ? <Link2 className="w-7 h-7 text-cyan-400" />
                : <Settings className="w-7 h-7 text-amber-400 animate-spin" style={{ animationDuration: '3s' }} />
              }
            </div>
            <h3 className="text-xl font-bold text-white">Broker Integration</h3>
            <p className={`${isActiveBroker ? 'text-cyan-400' : 'text-amber-400'} font-medium text-sm mt-1`}>
              {isActiveBroker ? 'Connect Your Broker' : 'Under Development'}
            </p>
          </div>

          {/* Broker Selector */}
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">Select Your Broker</p>
          <div className="space-y-1.5 mb-5">
            {BROKERS.map(b => (
              <button
                key={b.id}
                onClick={() => {
                  setWipBroker(b.id);
                  try { localStorage.setItem('algogreeks_wip_broker', b.id); } catch (_e) { /* ignore */ }
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all ${
                  wipBroker === b.id
                    ? `${b.bg} ${b.border} ring-1 ring-${b.color.replace('text-', '')}`
                    : 'bg-gray-800/30 border-gray-700/30 hover:bg-gray-800/50'
                }`}
                data-testid={`wip-broker-${b.id}`}
              >
                <div className={`w-2 h-2 rounded-full ${wipBroker === b.id ? 'bg-amber-400 animate-pulse' : 'bg-gray-600'}`} />
                <span className={`text-sm font-medium ${wipBroker === b.id ? b.color : 'text-gray-500'}`}>{b.name}</span>
                <span className="text-[10px] text-gray-600 ml-auto">{b.subtitle}</span>
                {savedBrokers[b.id]?.configured && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                )}
                {b.status === 'active' && !savedBrokers[b.id]?.configured && (
                  <span className="text-[9px] text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded font-medium">ACTIVE</span>
                )}
                {b.status === 'first' && !savedBrokers[b.id]?.configured && (
                  <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded font-medium">PLANNED</span>
                )}
                {b.status === 'planned' && !savedBrokers[b.id]?.configured && (
                  <span className="text-[9px] text-gray-500 bg-gray-500/10 px-1.5 py-0.5 rounded font-medium">PLANNED</span>
                )}
              </button>
            ))}
          </div>

          {/* Selected Broker Details */}
          {wipBrokerData && (
            <div className={`rounded-lg border ${wipBrokerData.border} ${wipBrokerData.bg} p-4 mb-4`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={`text-sm font-bold ${wipBrokerData.color}`}>{wipBrokerData.name} — Required Credentials</h4>
                <a href={wipBrokerData.docsUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-gray-500 hover:text-cyan-400 underline underline-offset-2 flex items-center gap-1">
                  <Info className="w-3 h-3" /> Docs
                </a>
              </div>

              {/* Status Badge */}
              {currentSavedBroker?.configured && !hasUnsavedChanges && (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-green-500/5 border border-green-500/20" data-testid="broker-saved-status">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <div className="flex-1">
                    <p className="text-xs text-green-400 font-medium">Credentials Saved</p>
                    <p className="text-[10px] text-gray-500">Updated {currentSavedBroker.updated_at ? new Date(currentSavedBroker.updated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) + ' IST' : 'recently'}</p>
                  </div>
                </div>
              )}

              {/* Saved masked values display */}
              {currentSavedBroker?.configured && !hasUnsavedChanges && (
                <div className="space-y-2 mb-3">
                  {wipBrokerData.fields.map(field => (
                    <div key={field.key} className="flex items-center justify-between px-3 py-1.5 rounded bg-black/20">
                      <span className="text-[10px] text-gray-400">{field.label}</span>
                      <span className="text-xs text-gray-300 font-mono tracking-wider">
                        {currentSavedBroker.fields?.[field.key] || <span className="text-gray-600">not set</span>}
                      </span>
                    </div>
                  ))}
                  <p className="text-[9px] text-gray-600 mt-1">Enter new values below to update credentials</p>
                </div>
              )}
              
              {/* Credential Input Fields */}
              <div className="space-y-2.5">
                {wipBrokerData.fields.map(field => (
                  <div key={field.key}>
                    <label className="text-[10px] text-gray-400 mb-0.5 block">{field.label}</label>
                    <input
                      type={field.type}
                      placeholder={currentSavedBroker?.configured ? 'Enter new value to update...' : field.placeholder}
                      value={wipCredentials[wipBroker]?.[field.key] || ''}
                      onChange={(e) => setWipCredentials(prev => ({
                        ...prev,
                        [wipBroker]: { ...(prev[wipBroker] || {}), [field.key]: e.target.value }
                      }))}
                      className="w-full bg-black/30 border border-gray-700/50 rounded px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/50 transition-colors"
                      data-testid={`wip-field-${field.key}`}
                    />
                    {!currentSavedBroker?.configured && (
                      <p className="text-[9px] text-gray-600 mt-0.5">{field.help}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 mt-4">
                {isActiveBroker ? (
                  /* Active broker (Dhan): Single "Save & Validate" button */
                  <button
                    onClick={handleSaveAndValidate}
                    disabled={saving || testing || !hasUnsavedChanges}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                      hasUnsavedChanges && !saving && !testing
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/30 hover:border-cyan-400/60'
                        : 'bg-gray-800/50 text-gray-600 border border-gray-700/30 cursor-not-allowed'
                    }`}
                    data-testid="save-and-validate-btn"
                  >
                    {(saving || testing) ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {saving && !testing ? 'Saving...' : 'Validating with Dhan...'}
                      </>
                    ) : (
                      <>
                        <Wifi className="w-4 h-4" />
                        Save & Validate Connection
                      </>
                    )}
                  </button>
                ) : (
                  /* Planned brokers: Separate Save + Validate */
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saving || !hasUnsavedChanges}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium transition-all ${
                        hasUnsavedChanges
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                          : 'bg-gray-800/50 text-gray-600 border border-gray-700/30 cursor-not-allowed'
                      }`}
                      data-testid="save-broker-credentials-btn"
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      {saving ? 'Saving...' : 'Save Credentials'}
                    </button>

                    {currentSavedBroker?.configured && (
                      <button
                        onClick={handleTestConnection}
                        disabled={testing}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all"
                        data-testid="test-broker-connection-btn"
                      >
                        {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
                        {testing ? 'Testing...' : 'Validate'}
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`mt-3 px-3 py-2 rounded-lg text-xs flex items-start gap-2 ${
                  testResult.status === 'ok' 
                    ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                    : testResult.status === 'warning'
                    ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`} data-testid="test-result">
                  {testResult.status === 'ok' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> :
                   testResult.status === 'warning' ? <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> :
                   <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  <div>
                    <p className="font-medium">{testResult.message}</p>
                    {testResult.issues?.map((issue, i) => (
                      <p key={i} className="text-[10px] opacity-80 mt-0.5">• {issue}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Auth Flow Info */}
              <div className="mt-3 pt-3 border-t border-gray-700/30">
                <p className="text-[10px] text-gray-500">
                  <span className="text-gray-400 font-medium">Auth Flow:</span> {wipBrokerData.authFlow}
                </p>
                {wipBrokerData.note && (
                  <p className="text-[10px] text-amber-400/60 mt-1 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    {wipBrokerData.note}
                  </p>
                )}
              </div>
            </div>
          )}

          <p className="text-gray-500 text-xs text-center">
            {isActiveBroker ? 'Enter your credentials and validate to connect' : 'Use signals from Telegram / UI for manual trading until broker integration is ready'}
          </p>
        </div>
      </div>
      )}

      {/* Dhan Connected Success State */}
      {dhanConnected && wipBroker === 'dhan' && (
        <div className="mb-6 space-y-3">
          {/* Apr-2026 B-UX-1: "Change Broker" widget for one-off broker swaps.
              Active broker stays front-and-center; switching is one click away. */}
          <div className="flex items-center justify-end mb-2" data-testid="change-broker-widget">
            <div className="relative">
              <button
                onClick={() => setShowChangeBrokerMenu(v => !v)}
                className="text-[11px] px-3 py-1.5 rounded-full bg-gray-800/60 border border-gray-700 text-gray-300 hover:border-cyan-500/40 hover:text-cyan-300 transition flex items-center gap-1.5"
                data-testid="change-broker-toggle"
              >
                Using <span className="text-cyan-400 font-semibold">Dhan</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showChangeBrokerMenu ? 'rotate-180' : ''}`} />
              </button>
              {showChangeBrokerMenu && (
                <div className="absolute right-0 mt-2 w-60 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-30 py-1" data-testid="change-broker-menu">
                  <p className="px-3 py-2 text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-800">Switch broker</p>
                  {BROKERS.filter(b => b.id !== 'dhan').map(b => (
                    <button
                      key={b.id}
                      onClick={() => {
                        setWipBroker(b.id);
                        try { localStorage.setItem('algogreeks_wip_broker', b.id); } catch (_e) { /* ignore */ }
                        setShowChangeBrokerMenu(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-300 hover:bg-gray-800 transition"
                      data-testid={`switch-broker-${b.id}`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full ${b.status === 'active' ? 'bg-cyan-400' : 'bg-gray-600'}`} />
                      <span className={b.color}>{b.name}</span>
                      <span className="text-[10px] text-gray-600 ml-auto">
                        {savedBrokers[b.id]?.configured ? 'configured' : (b.status === 'planned' ? 'planned' : '')}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Session 147j+17l — Connection Status now reflects LIVE
              token health, not just "credentials exist in DB". Colour
              maps to `/api/broker/dhan/health`.status. */}
          {(() => {
            const s = dhanHealth.status;
            const isHealthy = s === 'healthy';
            const isExpired = s === 'token_expired';
            const isMismatch = s === 'ip_mismatch' || s === 'ip_missing';
            const isChecking = s === 'unknown' && dhanHealth.checking;
            const isBadOther = !isHealthy && !isExpired && !isMismatch && !isChecking;
            const tone = isHealthy ? 'green'
              : isExpired ? 'rose'
              : isMismatch ? 'amber'
              : 'gray';
            const dot = isHealthy ? '🟢' : isExpired ? '🔴' : isMismatch ? '🟡' : '⚪';
            const label = isHealthy ? 'Dhan Connected'
              : isExpired ? 'Dhan Token Expired'
              : isMismatch ? 'Dhan IP Mismatch'
              : isChecking ? 'Checking Dhan…'
              : (dhanHealth.message ? `Dhan: ${dhanHealth.message.slice(0,40)}` : 'Dhan Connected');
            const bg = tone === 'green' ? 'bg-green-500/5 border-green-500/30'
              : tone === 'rose' ? 'bg-rose-500/10 border-rose-500/50'
              : tone === 'amber' ? 'bg-amber-500/10 border-amber-500/40'
              : 'bg-gray-500/5 border-gray-500/30';
            const iconBg = tone === 'green' ? 'bg-green-500/10' : tone === 'rose' ? 'bg-rose-500/15' : tone === 'amber' ? 'bg-amber-500/15' : 'bg-gray-500/10';
            const iconClr = tone === 'green' ? 'text-green-400' : tone === 'rose' ? 'text-rose-400' : tone === 'amber' ? 'text-amber-400' : 'text-gray-400';
            const labelClr = tone === 'green' ? 'text-green-400' : tone === 'rose' ? 'text-rose-400' : tone === 'amber' ? 'text-amber-400' : 'text-gray-300';
            return (
              <div className={`p-4 rounded-xl border ${bg}`} data-testid="dhan-connected-banner" data-status={s}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center`}>
                    <span className="text-lg">{dot}</span>
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-bold ${labelClr}`}>{label}</p>
                    <p className="text-xs text-gray-400">
                      {dhanHealth.last_check
                        ? `Last live-check: ${new Date(dhanHealth.last_check).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST`
                        : (savedBrokers.dhan?.updated_at
                          ? `Last saved: ${new Date(savedBrokers.dhan.updated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST`
                          : 'Live-check pending…')}
                      {isBadOther && dhanHealth.message ? ` · ${dhanHealth.message}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => setDhanConnected(false)}
                    className={`text-xs underline underline-offset-2 ${
                      isExpired
                        ? 'text-rose-300 hover:text-rose-200 font-semibold animate-pulse'
                        : 'text-gray-500 hover:text-cyan-400'
                    }`}
                    data-testid="dhan-reconnect-btn"
                  >
                    {isExpired ? '⚠ Update Token now' : 'Update Token'}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Static IP Registration (SEBI Requirement) */}
          <div className="p-4 rounded-xl bg-gray-800/30 border border-gray-700/50" data-testid="dhan-ip-section">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-amber-400" />
              <h4 className="text-sm font-bold text-white">Static IP Registration</h4>
              <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded font-medium">SEBI Required</span>
            </div>
            <p className="text-[10px] text-gray-500 mb-3">
              SEBI mandates a whitelisted static IP for all API orders. One-time setup — register your server's IP with Dhan.
            </p>

            {/* Current IPs from Dhan */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-black/20 rounded-lg p-2.5">
                <div className="text-[9px] text-gray-500 uppercase mb-1">Primary IP (Dhan)</div>
                {dhanIp.loading ? (
                  <Loader2 className="w-3 h-3 animate-spin text-gray-500" />
                ) : dhanIp.primary ? (
                  <div className="text-xs font-mono text-green-400">{dhanIp.primary}</div>
                ) : (
                  <div className="text-xs text-gray-600">Not registered</div>
                )}
              </div>
              <div className="bg-black/20 rounded-lg p-2.5">
                <div className="text-[9px] text-gray-500 uppercase mb-1">This Server's IP</div>
                {detectedIp.loading ? (
                  <Loader2 className="w-3 h-3 animate-spin text-gray-500" />
                ) : detectedIp.ip ? (
                  <div className="text-xs font-mono text-cyan-400">{detectedIp.ip}</div>
                ) : (
                  <div className="text-xs text-gray-600">Detecting...</div>
                )}
              </div>
            </div>

            {/* Status + Action */}
            {dhanIp.primary && detectedIp.ip && dhanIp.primary === detectedIp.ip ? (
              /* IP already registered and matches */
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/5 border border-green-500/20">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <p className="text-xs text-green-400 font-medium">Server IP registered with Dhan. Auto-trading ready.</p>
              </div>
            ) : detectedIp.ip ? (
              /* IP available but not registered, or different */
              <button
                onClick={handleRegisterIp}
                disabled={registeringIp}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                  !registeringIp
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 hover:bg-amber-500/30'
                    : 'bg-gray-800/50 text-gray-600 border border-gray-700/30'
                }`}
                data-testid="register-ip-btn"
              >
                {registeringIp ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Registering with Dhan...</>
                ) : (
                  <><Shield className="w-4 h-4" /> Register Server IP with Dhan</>
                )}
              </button>
            ) : (
              <p className="text-[10px] text-gray-600">Detecting server IP...</p>
            )}

            {dhanIp.primary && detectedIp.ip && dhanIp.primary !== detectedIp.ip && (
              <div className="flex items-start gap-2 mt-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber-400">
                  Registered IP ({dhanIp.primary}) differs from current server IP ({detectedIp.ip}). Click above to update.
                </p>
              </div>
            )}

            {dhanIp.error && (
              <p className="text-[10px] text-red-400 mt-2">{dhanIp.error}</p>
            )}
          </div>
        </div>
      )}

      {/* Actual UI (visible behind overlay when not connected, or shown when connected) */}
      <div className={`space-y-6 ${showOverlay ? 'opacity-60' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link2 className="w-6 h-6 text-cyan-400" />
            <div>
              <h2 className="text-lg font-bold text-white">Broker Integration</h2>
              <p className="text-xs text-gray-500">Connect your broker for auto-execution of signals</p>
            </div>
          </div>
          <span className="px-3 py-1 text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg">
            WORK IN PROGRESS
          </span>
        </div>

        {/* Daily Activation */}
        <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-5" data-testid="daily-activation">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {dailyActive ? (
                <button onClick={() => handleDailyToggle(false)} className="w-14 h-8 bg-green-500/20 border border-green-500/30 rounded-full relative transition-all" data-testid="daily-toggle">
                  <div className="w-6 h-6 bg-green-400 rounded-full absolute right-1 top-1 flex items-center justify-center">
                    <Power className="w-3 h-3 text-black" />
                  </div>
                </button>
              ) : (
                <button onClick={() => handleDailyToggle(true)} className="w-14 h-8 bg-gray-700/50 border border-gray-600/50 rounded-full relative transition-all" data-testid="daily-toggle">
                  <div className="w-6 h-6 bg-gray-500 rounded-full absolute left-1 top-1 flex items-center justify-center">
                    <PowerOff className="w-3 h-3 text-gray-300" />
                  </div>
                </button>
              )}
              <div>
                <p className="text-sm font-semibold text-white">
                  {dailyActive ? 'Auto-Trading is ON for Today' : 'Auto-Trading is OFF for Today'}
                </p>
                <p className="text-xs text-gray-500">
                  {dailyActive 
                    ? 'Signals will be auto-executed via your broker' 
                    : 'Activate before market open to enable auto-execution'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Clock className="w-3 h-3" />
              Auto-resets at 3:31 PM IST
            </div>
          </div>
        </div>

        {/* Broker Connection */}
        <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-5" data-testid="broker-connection">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-cyan-400" />
            Broker Connection
          </h3>
          
          <div className="relative mb-4">
            <label className="text-xs text-gray-500 mb-1.5 block">Select Broker</label>
            <button 
              onClick={() => setShowBrokerDropdown(!showBrokerDropdown)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg ${broker.bg} border ${broker.border} transition-colors`}
              data-testid="broker-selector"
            >
              <div className="flex items-center gap-3">
                <span className={`font-semibold text-sm ${broker.color}`}>{broker.name}</span>
                <span className="text-xs text-gray-500">{broker.subtitle}</span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
            {showBrokerDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg overflow-hidden z-10 shadow-xl">
                {BROKERS.map(b => (
                  <button
                    key={b.id}
                    onClick={() => { setSelectedBroker(b.id); setShowBrokerDropdown(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-700/50 transition-colors ${
                      b.id === selectedBroker ? 'bg-gray-700/30' : ''
                    }`}
                  >
                    <span className={b.color}>{b.name}</span>
                    <span className="text-xs text-gray-600">{b.subtitle}</span>
                    {b.id === selectedBroker && <Check className="w-3 h-3 text-green-400 ml-auto" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2" data-testid="broker-connection-status">
              {dhanConnected && selectedBroker === 'dhan' ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-green-400 font-medium">Connected</span>
                  {savedBrokers.dhan?.updated_at && (
                    <span className="text-[10px] text-gray-500 ml-1">
                      · validated {new Date(savedBrokers.dhan.updated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST
                    </span>
                  )}
                </>
              ) : (
                <>
                  <div className="w-2 h-2 rounded-full bg-gray-500" />
                  <span className="text-xs text-gray-500">Not Connected</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Trading Preferences */}
        <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-5" data-testid="trading-preferences">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="w-4 h-4 text-cyan-400" />
            Trading Preferences
          </h3>

          <div className="rounded-lg border border-gray-700/50 overflow-hidden mb-4">
            <div className="grid grid-cols-5 bg-gray-800/50 px-4 py-2.5">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Security Type</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium text-center">Enable</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium text-center">BUY</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium text-center">SELL</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium text-center">LOTS</span>
            </div>
            {SECURITY_TYPES.map(sec => (
              <div key={sec.id} className="grid grid-cols-5 items-center px-4 py-3 border-t border-gray-700/30 hover:bg-gray-800/20">
                <div>
                  <p className="text-sm text-white">{sec.label}</p>
                  <p className="text-[10px] text-gray-600">{sec.description}</p>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={() => updatePreference(sec.id, 'enabled', !preferences[sec.id].enabled)}
                    className={`w-5 h-5 rounded border transition-colors flex items-center justify-center ${
                      preferences[sec.id].enabled 
                        ? 'bg-cyan-500/20 border-cyan-500/50' 
                        : 'bg-gray-800/50 border-gray-600/50'
                    }`}
                    data-testid={`enable-${sec.id}`}
                  >
                    {preferences[sec.id].enabled && <Check className="w-3 h-3 text-cyan-400" />}
                  </button>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={() => updatePreference(sec.id, 'buy', !preferences[sec.id].buy)}
                    className={`w-5 h-5 rounded border transition-colors flex items-center justify-center ${
                      preferences[sec.id].buy 
                        ? 'bg-green-500/20 border-green-500/50' 
                        : 'bg-gray-800/50 border-gray-600/50'
                    }`}
                    data-testid={`buy-${sec.id}`}
                  >
                    {preferences[sec.id].buy && <Check className="w-3 h-3 text-green-400" />}
                  </button>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={() => handleSellToggle(sec.id)}
                    className={`w-5 h-5 rounded border transition-colors flex items-center justify-center ${
                      preferences[sec.id].sell 
                        ? 'bg-red-500/20 border-red-500/50' 
                        : 'bg-gray-800/50 border-gray-600/50'
                    }`}
                    data-testid={`sell-${sec.id}`}
                  >
                    {preferences[sec.id].sell && <Check className="w-3 h-3 text-red-400" />}
                  </button>
                </div>
                <div className="flex justify-center">
                  <select
                    value={preferences[sec.id].lots || 1}
                    onChange={(e) => updatePreference(sec.id, 'lots', parseInt(e.target.value))}
                    className="bg-gray-800/50 border border-gray-700/50 rounded px-2 py-1 text-xs text-white text-center focus:outline-none focus:border-cyan-500/50 appearance-none cursor-pointer w-14"
                    data-testid={`lots-${sec.id}`}
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>

          {hasSellEnabled && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 mb-4" data-testid="sell-warning">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-amber-400 font-medium">Margin Required for SELL Positions</p>
                <p className="text-[10px] text-amber-400/70 mt-0.5">Ensure your broker account has sufficient margin balance.</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Max Trades Per Day</p>
              <p className="text-[10px] text-gray-600">System stops executing after reaching this limit</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={maxTrades}
                onChange={(e) => { setMaxTrades(parseInt(e.target.value)); setPrefsDirty(true); }}
                className="bg-gray-800/50 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 appearance-none cursor-pointer"
                data-testid="max-trades-select"
              >
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <div className="text-xs text-gray-500">
                <span className="text-white font-mono">0</span> / {maxTrades} today
              </div>
            </div>
          </div>

          {/* Slippage Buffer */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700/30">
            <div>
              <p className="text-sm text-white">Slippage Buffer</p>
              <p className="text-[10px] text-gray-600">Limit price offset from LTP for order fill guarantee</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={slippageBuffer}
                onChange={(e) => { setSlippageBuffer(parseFloat(e.target.value)); setPrefsDirty(true); }}
                className="bg-gray-800/50 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 appearance-none cursor-pointer"
                data-testid="slippage-buffer-select"
              >
                {[0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0].map(n => (
                  <option key={n} value={n}>{n}%</option>
                ))}
              </select>
            </div>
          </div>

          {/* Auto SL Order */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700/30">
            <div>
              <p className="text-sm text-white">Auto SL Order</p>
              <p className="text-[10px] text-gray-600">Place SL-LIMIT order immediately after entry fills</p>
            </div>
            <button
              onClick={() => { setAutoSlOrder(!autoSlOrder); setPrefsDirty(true); }}
              className={`w-5 h-5 rounded border transition-colors flex items-center justify-center ${
                autoSlOrder ? 'bg-green-500/20 border-green-500/50' : 'bg-gray-800/50 border-gray-600/50'
              }`}
              data-testid="auto-sl-toggle"
            >
              {autoSlOrder && <Check className="w-3 h-3 text-green-400" />}
            </button>
          </div>

          {/* Save Button */}
          {prefsDirty && (
            <div className="mt-4 pt-4 border-t border-gray-700/30">
              <button
                onClick={savePreferences}
                disabled={savingPrefs}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/30 transition-all"
                data-testid="save-preferences-btn"
              >
                {savingPrefs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {savingPrefs ? 'Saving...' : 'Save Trading Preferences'}
              </button>
            </div>
          )}
        </div>

        {/* Trade Log + P&L */}
        <TradeLog authFetch={authFetch} />
      </div>
    </div>
  );
};


// ============================================
// TRADE LOG COMPONENT (SESSION 137 Phase D6)
// ============================================

const STATUS_STYLES = {
  OPEN: { text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', label: 'OPEN' },
  OPEN_NO_SL: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'NO SL' },
  PENDING: { text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', label: 'PENDING' },
  CLOSED: { text: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30', label: 'CLOSED' },
  FAILED: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'FAILED' },
};

const EXIT_LABELS = {
  T1_HIT: { text: 'text-green-400', label: 'T1 Hit' },
  SL_HIT: { text: 'text-red-400', label: 'SL Hit' },
  MANUAL_EXIT: { text: 'text-cyan-400', label: 'Manual' },
  EOD_SQUARE_OFF: { text: 'text-amber-400', label: 'EOD' },
  CLOSED_DURING_RESTART: { text: 'text-purple-400', label: 'Restart' },
};

const TradeLog = ({ authFetch }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('today'); // 'today' or 'history'
  const [history, setHistory] = useState({ trades: [], daily_summary: [] });

  const fetchTrades = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/api/broker/trades/today`);
      if (res.ok) {
        const data = await res.json();
        setTrades(data.trades || []);
      }
    } catch (err) {
      console.error('Trade log fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/api/broker/trades/history?days=30`);
      if (res.ok) {
        const data = await res.json();
        setHistory({ trades: data.trades || [], daily_summary: data.daily_summary || [] });
      }
    } catch (err) {
      console.error('Trade history fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchTrades();
    const interval = setInterval(fetchTrades, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, [fetchTrades]);

  useEffect(() => {
    if (view === 'history') fetchHistory();
  }, [view, fetchHistory]);

  // Today's P&L summary
  const todaySummary = trades.reduce((acc, t) => {
    acc.total++;
    const pnl = t.pnl || 0;
    acc.pnl += pnl;
    if (pnl > 0) acc.wins++;
    else if (pnl < 0) acc.losses++;
    if (t.status === 'OPEN' || t.status === 'OPEN_NO_SL') acc.open++;
    return acc;
  }, { total: 0, pnl: 0, wins: 0, losses: 0, open: 0 });

  const formatTime = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return '-'; }
  };

  return (
    <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-5" data-testid="execution-log">
      {/* Header with tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-cyan-400" />
          <div className="flex items-center gap-1 bg-gray-800/50 rounded-lg p-0.5">
            <button
              onClick={() => setView('today')}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                view === 'today' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-500 hover:text-gray-300'
              }`}
              data-testid="trade-log-today-tab"
            >
              Today
            </button>
            <button
              onClick={() => setView('history')}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                view === 'history' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-500 hover:text-gray-300'
              }`}
              data-testid="trade-log-history-tab"
            >
              History
            </button>
          </div>
        </div>
        <button
          onClick={view === 'today' ? fetchTrades : fetchHistory}
          className="p-1.5 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:bg-gray-700/50 transition-colors"
          data-testid="trade-log-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {view === 'today' ? (
        <>
          {/* Today's P&L Summary */}
          {trades.length > 0 && (
            <div className="grid grid-cols-5 gap-2 mb-4" data-testid="today-pnl-summary">
              <div className="bg-black/20 rounded-lg p-2.5 text-center">
                <div className="text-[9px] text-gray-500 uppercase">Trades</div>
                <div className="text-lg font-bold text-white font-mono">{todaySummary.total}</div>
              </div>
              <div className="bg-black/20 rounded-lg p-2.5 text-center">
                <div className="text-[9px] text-gray-500 uppercase">Open</div>
                <div className="text-lg font-bold text-cyan-400 font-mono">{todaySummary.open}</div>
              </div>
              <div className="bg-black/20 rounded-lg p-2.5 text-center">
                <div className="text-[9px] text-gray-500 uppercase">Wins</div>
                <div className="text-lg font-bold text-green-400 font-mono">{todaySummary.wins}</div>
              </div>
              <div className="bg-black/20 rounded-lg p-2.5 text-center">
                <div className="text-[9px] text-gray-500 uppercase">Losses</div>
                <div className="text-lg font-bold text-red-400 font-mono">{todaySummary.losses}</div>
              </div>
              <div className={`rounded-lg p-2.5 text-center ${todaySummary.pnl >= 0 ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
                <div className="text-[9px] text-gray-500 uppercase">P&L</div>
                <div className={`text-lg font-bold font-mono ${todaySummary.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {todaySummary.pnl >= 0 ? '+' : ''}{todaySummary.pnl.toFixed(0)}
                </div>
              </div>
            </div>
          )}

          {/* Trade rows */}
          {trades.length > 0 ? (
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {trades.map((trade, idx) => {
                const status = STATUS_STYLES[trade.status] || STATUS_STYLES.PENDING;
                const exitLabel = EXIT_LABELS[trade.exit_reason] || null;
                const isBuy = trade.direction === 'BUY';
                const pnl = trade.pnl || 0;
                return (
                  <div
                    key={idx}
                    className={`rounded-lg border ${status.border} ${status.bg} px-3 py-2.5 flex items-center gap-3`}
                    data-testid={`trade-row-${idx}`}
                  >
                    {/* Direction + Symbol */}
                    <div className="flex items-center gap-2 min-w-[140px]">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${isBuy ? 'bg-green-500/15' : 'bg-red-500/15'}`}>
                        {isBuy
                          ? <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                          : <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-white">{trade.symbol}</div>
                        <div className="text-[9px] text-gray-500">{trade.trading_symbol?.slice(0, 20) || trade.signal_source}</div>
                      </div>
                    </div>

                    {/* Entry */}
                    <div className="text-center min-w-[60px]">
                      <div className="text-[8px] text-gray-600 uppercase">Entry</div>
                      <div className="text-xs font-mono text-white">
                        {(trade.entry_filled_price || trade.signal_entry_price || 0).toFixed(2)}
                      </div>
                    </div>

                    {/* SL / T1 */}
                    <div className="text-center min-w-[50px]">
                      <div className="text-[8px] text-gray-600 uppercase">SL</div>
                      <div className="text-xs font-mono text-red-400">{(trade.sl_price || 0).toFixed(1)}</div>
                    </div>
                    <div className="text-center min-w-[50px]">
                      <div className="text-[8px] text-gray-600 uppercase">T1</div>
                      <div className="text-xs font-mono text-green-400">{(trade.t1_price || 0).toFixed(1)}</div>
                    </div>

                    {/* Qty */}
                    <div className="text-center min-w-[40px]">
                      <div className="text-[8px] text-gray-600 uppercase">Qty</div>
                      <div className="text-xs font-mono text-gray-300">{trade.quantity || 0}</div>
                    </div>

                    {/* Status */}
                    <div className="text-center min-w-[60px]">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${status.text} ${status.bg} border ${status.border}`}>
                        {status.label}
                      </span>
                      {exitLabel && (
                        <div className={`text-[9px] mt-0.5 ${exitLabel.text}`}>{exitLabel.label}</div>
                      )}
                    </div>

                    {/* P&L */}
                    <div className="text-center min-w-[65px] ml-auto">
                      {trade.status === 'CLOSED' && pnl !== 0 ? (
                        <div className={`text-sm font-bold font-mono ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {pnl >= 0 ? '+' : ''}{pnl.toFixed(0)}
                        </div>
                      ) : trade.status === 'FAILED' ? (
                        <Ban className="w-4 h-4 text-red-400 mx-auto" />
                      ) : (
                        <div className="text-xs text-gray-500 font-mono">-</div>
                      )}
                    </div>

                    {/* Time */}
                    <div className="text-[9px] text-gray-600 min-w-[55px] text-right">
                      {formatTime(trade.entry_time || trade.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="w-6 h-6 text-gray-700 mx-auto mb-2" />
              <p className="text-gray-600 text-xs">No trades executed today</p>
              <p className="text-gray-700 text-[10px] mt-1">Trades appear here when auto-trading is active</p>
            </div>
          )}
        </>
      ) : (
        /* History view */
        <>
          {history.daily_summary.length > 0 ? (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {/* Daily P&L cards */}
              {history.daily_summary.map((day, idx) => {
                const isPositive = day.pnl >= 0;
                return (
                  <div
                    key={idx}
                    className={`rounded-lg border ${isPositive ? 'border-green-500/20 bg-green-500/5' : 'border-red-500/20 bg-red-500/5'} px-4 py-3 flex items-center justify-between`}
                    data-testid={`history-day-${idx}`}
                  >
                    <div>
                      <div className="text-sm font-bold text-white">
                        {new Date(day.date + 'T00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {day.trades} trade{day.trades !== 1 ? 's' : ''} | W:{day.wins} L:{day.losses}
                      </div>
                    </div>
                    <div className={`text-lg font-bold font-mono ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                      {isPositive ? '+' : ''}{day.pnl.toFixed(0)}
                    </div>
                  </div>
                );
              })}

              {/* Cumulative */}
              {history.daily_summary.length > 1 && (
                <div className="mt-3 pt-3 border-t border-gray-700/30 flex items-center justify-between px-2">
                  <span className="text-xs text-gray-500">Cumulative ({history.daily_summary.length} days)</span>
                  <span className={`text-sm font-bold font-mono ${
                    history.daily_summary.reduce((s, d) => s + d.pnl, 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {(() => {
                      const total = history.daily_summary.reduce((s, d) => s + d.pnl, 0);
                      return `${total >= 0 ? '+' : ''}${total.toFixed(0)}`;
                    })()}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="w-6 h-6 text-gray-700 mx-auto mb-2" />
              <p className="text-gray-600 text-xs">No trade history yet</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};


export default BrokerIntegrationTab;
